import React, { useRef } from "react";

import { formatDate } from "../../../shared/utility";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

interface WorkOrder {
  id: number;
  work_number: string;
  employee_name: string;
  start_date: string;
  end_date: string;
  reason: string;
  make: string;
  model: string;
  type: string;
  serial: string;
  work_performed: string;
  recommendations: string;
  notes: string;
}

interface CustomerWorkOrdersTabProps {
  workOrders: WorkOrder[];
  openDetails: (workOrder: WorkOrder) => {};
  getPreviousWorkOrders: (search: String) => {};
}

export default function CustomerWorkOrdersTab({
  workOrders,
  openDetails,
  getPreviousWorkOrders,
}: CustomerWorkOrdersTabProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <div className="justify-between space-y-2">
      <div className="space-y-2">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Previous Work Orders:
        </h3>

        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div className="relative flex items-stretch flex-grow focus-within:z-10">
            <input
              type="search"
              name="searchInput"
              id="searchInput"
              className="block w-full rounded-lg rounded-l-md p-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
              placeholder="Search by name"
              ref={searchRef}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchRef.current?.value) {
                  getPreviousWorkOrders(searchRef.current.value);
                }
              }}
            />
          </div>
          <button
            type="button"
            className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
            onClick={() => {
              if (searchRef.current?.value) {
                getPreviousWorkOrders(searchRef.current.value);
              }
            }}
          >
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <div className="overflow-hidden card-custom">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-white">
            <tr>
              <th
                scope="col"
                className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
              >
                WO #
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
              >
                Reason
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
              >
                Technician
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
              >
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {workOrders.length > 0 ? (
              workOrders.map((workOrder) => (
                <tr key={workOrder.id}>
                  <td className="whitespace-nowrap py-1.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {workOrder.work_number}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                    {workOrder.reason}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                    {workOrder.employee_name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                    {formatDate(workOrder.start_date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                    <button
                      onClick={() => {
                        openDetails(workOrder);
                      }}
                      className="ml-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 text-center"
                  colSpan={4}
                >
                  No Previous Work Orders
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
